// Evidence Library — physician-reviewed topic pages with graded citations.
// Grades: A = consistent RCT/meta-analytic support · B = strong observational/mixed
// C = limited/early human data · Emerging = active research, not yet established.

export const EVIDENCE_PAGES = {
  "biological-age-epigenetic-clocks": {
    slug: "biological-age-epigenetic-clocks",
    metaTitle: "Biological Age & Epigenetic Clocks — Evidence Review | Integrated Wellness",
    metaDescription:
      "What do methylation clocks actually measure? Peer-reviewed evidence on epigenetic biological age, its limitations, and why multi-modal assessment matters.",
    eyebrow: "Evidence · Longevity",
    h1: "Biological Age & Epigenetic Clocks",
    grade: "B",
    directAnswer:
      "Epigenetic clocks estimate biological age from DNA methylation patterns and are among the best-validated molecular aging biomarkers: second- and third-generation clocks (PhenoAge, GrimAge) predict morbidity and mortality better than chronological age. Their key limitation is that a molecular estimate can diverge from vascular function and real-world physiological reserve — which is why Integrated Wellness assesses biological age across three independent pillars.",
    sections: [
      {
        h: "What methylation clocks measure",
        body: "DNA methylation is a chemical modification that regulates gene expression and shifts in reproducible ways with age. Horvath's 2013 multi-tissue clock showed that a few hundred methylation sites can estimate age with remarkable accuracy across tissues. Later clocks were trained not on calendar age but on clinical phenotypes (PhenoAge) and mortality risk (GrimAge), making them more relevant to health outcomes.",
      },
      {
        h: "Clinical relevance",
        body: "Accelerated epigenetic age — a clock estimate older than chronological age — is associated with higher all-cause mortality, cardiovascular disease and functional decline in large cohorts. This makes methylation clocks useful for risk stratification and for tracking direction of change over time.",
      },
      {
        h: "Limitations",
        body: "Single-clock estimates carry technical noise between test batches; different clocks can disagree for the same person; and a favorable molecular age does not guarantee preserved vascular function or physical reserve. Interventional evidence that lowering epigenetic age causes better outcomes is still limited — clocks are best treated as biomarkers, not outcomes in themselves.",
      },
      {
        h: "The Integrated Wellness approach",
        body: "Our patent-pending Integrated Biological Age 2.0 engine runs 30+ methylation clocks in parallel for the epigenetic pillar (40% weight), then reconciles the result against an AI-derived vascular age pillar (35%) and a behavioral reserve score (25%). Divergence between pillars is treated as a clinically meaningful finding — flagged for physician review rather than averaged away.",
      },
    ],
    evidenceSummary: [
      { finding: "Methylation clocks estimate age accurately across tissues", grade: "A" },
      { finding: "Age acceleration predicts mortality & morbidity in cohorts", grade: "B" },
      { finding: "Interventions reliably improve epigenetic age", grade: "Emerging" },
      { finding: "Multi-modal (3-pillar) assessment outperforms single-clock", grade: "Emerging" },
    ],
    references: [
      {
        authors: "Horvath S",
        year: 2013,
        title: "DNA methylation age of human tissues and cell types",
        journal: "Genome Biology",
        url: "https://pubmed.ncbi.nlm.nih.gov/24138928/",
      },
      {
        authors: "Levine ME, et al.",
        year: 2018,
        title: "An epigenetic biomarker of aging for lifespan and healthspan (PhenoAge)",
        journal: "Aging (Albany NY)",
        url: "https://pubmed.ncbi.nlm.nih.gov/29676998/",
      },
      {
        authors: "Lu AT, et al.",
        year: 2019,
        title: "DNA methylation GrimAge strongly predicts lifespan and healthspan",
        journal: "Aging (Albany NY)",
        url: "https://pubmed.ncbi.nlm.nih.gov/30669119/",
      },
    ],
    related: [
      { label: "Biological Age Testing", href: "/biological-age-testing" },
      { label: "IBA 2.0 Technology", href: "/biological-age" },
      { label: "Longevity Medicine", href: "/longevity-medicine" },
    ],
  },

  "insulin-resistance": {
    slug: "insulin-resistance",
    metaTitle: "Insulin Resistance & Metabolic Health — Evidence Review | Integrated Wellness",
    metaDescription:
      "The evidence behind early insulin-resistance testing: fasting insulin, CGM targets, and why most adults are not metabolically healthy. Physician-reviewed.",
    eyebrow: "Evidence · Metabolic",
    h1: "Insulin Resistance & Metabolic Health",
    grade: "A",
    directAnswer:
      "Insulin resistance — reduced cellular response to insulin — is the central driver of type 2 diabetes and a major contributor to cardiovascular risk, described by Reaven's landmark 1988 Banting Lecture. It develops years before glucose becomes abnormal, and population data suggest only about 1 in 8 American adults meets criteria for optimal metabolic health, making early measurement one of the highest-yield actions in preventive medicine.",
    sections: [
      {
        h: "What the science established",
        body: "Reaven's syndrome-X framework connected insulin resistance to hypertension, dyslipidemia and cardiovascular disease decades before it was mainstream. Modern cohort data confirm the downstream consequences, and NHANES-based analysis (Araújo 2019) found only ~12% of US adults achieve optimal levels across five metabolic-health criteria.",
      },
      {
        h: "Why early testing matters",
        body: "Fasting insulin rises long before fasting glucose or HbA1c leaves the normal range, because the pancreas compensates by secreting more insulin. Measuring fasting insulin (and derived indices such as HOMA-IR) alongside post-meal glucose patterns surfaces dysfunction in the compensated phase — when lifestyle intervention is most effective.",
      },
      {
        h: "The role of CGM",
        body: "Continuous glucose monitoring shows real-world glycemic response to actual meals, exercise, stress and sleep. International consensus (Battelino 2019) defines time-in-range and variability targets, giving CGM data a rigorous interpretive framework beyond wellness anecdotes.",
      },
      {
        h: "Limitations",
        body: "Fasting insulin assays are not fully standardized between labs, HOMA-IR is a surrogate rather than a clamp measurement, and CGM time-in-range targets were developed primarily for diabetes populations. Interpretation in metabolically healthy adults requires clinical context — which is why our results are physician-reviewed.",
      },
      {
        h: "The Integrated Wellness approach",
        body: "We combine fasting insulin, lipid fractionation, inflammatory markers and 2+ weeks of CGM data inside the Matrix Engine, read against genetics, body composition and recovery trends — then verify protocol response by retesting rather than by feel.",
      },
    ],
    evidenceSummary: [
      { finding: "Insulin resistance drives T2D & elevates CV risk", grade: "A" },
      { finding: "Fasting insulin rises years before glucose abnormality", grade: "B" },
      { finding: "Lifestyle intervention improves insulin sensitivity", grade: "A" },
      { finding: "CGM-guided nutrition in non-diabetics improves outcomes", grade: "Emerging" },
    ],
    references: [
      {
        authors: "Reaven GM",
        year: 1988,
        title: "Banting Lecture: Role of insulin resistance in human disease",
        journal: "Diabetes",
        url: "https://pubmed.ncbi.nlm.nih.gov/3056758/",
      },
      {
        authors: "Araújo J, Cai J, Stevens J",
        year: 2019,
        title: "Prevalence of optimal metabolic health in American adults (NHANES 2009–2016)",
        journal: "Metabolic Syndrome and Related Disorders",
        url: "https://pubmed.ncbi.nlm.nih.gov/30484738/",
      },
      {
        authors: "Battelino T, et al.",
        year: 2019,
        title: "Clinical targets for continuous glucose monitoring data interpretation",
        journal: "Diabetes Care",
        url: "https://pubmed.ncbi.nlm.nih.gov/31177185/",
      },
    ],
    related: [
      { label: "Metabolic Health", href: "/metabolic-health" },
      { label: "Metabolic Optimization Program", href: "/programs/metabolic-optimization" },
    ],
  },

  "testosterone-therapy": {
    slug: "testosterone-therapy",
    metaTitle: "Testosterone Therapy — Evidence & Safety Review | Integrated Wellness",
    metaDescription:
      "What does the evidence say about testosterone therapy? TRAVERSE cardiovascular safety data, Endocrine Society guidance, benefits and limitations — physician-reviewed.",
    eyebrow: "Evidence · Hormones",
    h1: "Testosterone Therapy: Evidence & Safety",
    grade: "A",
    directAnswer:
      "For men with confirmed hypogonadism, testosterone therapy improves sexual function, mood, bone density and lean mass, per Endocrine Society clinical practice guidelines. The 2023 TRAVERSE trial — over 5,000 men at elevated cardiovascular risk — found testosterone non-inferior to placebo for major adverse cardiac events, addressing the field's largest safety question. Therapy still requires proper diagnosis, conservative titration and scheduled monitoring.",
    sections: [
      {
        h: "What the evidence supports",
        body: "Guideline-concordant therapy in men with symptomatic, biochemically confirmed hypogonadism improves libido, erectile function, mood, bone mineral density and body composition. Benefits are most consistent when baseline testosterone is genuinely low — not when normal levels are pushed supraphysiologic.",
      },
      {
        h: "The cardiovascular safety question",
        body: "For a decade, conflicting observational signals left cardiovascular safety unresolved. TRAVERSE (Lincoff 2023, NEJM) randomized 5,246 middle-aged and older men with hypogonadism and elevated CV risk to transdermal testosterone or placebo: testosterone was non-inferior for major adverse cardiac events, though atrial fibrillation, acute kidney injury and pulmonary embolism signals warrant continued monitoring.",
      },
      {
        h: "Limitations",
        body: "Testosterone is not an anti-aging drug for men with normal levels; evidence there is weak. Erythrocytosis, fertility suppression and prostate monitoring remain practical considerations. Long-term outcome data beyond ~3 years of randomized follow-up are limited.",
      },
      {
        h: "The Integrated Wellness approach",
        body: "Balance, never blast: full-axis testing (total and free testosterone, SHBG, estradiol, thyroid, metabolic context), physician-confirmed diagnosis, conservative dosing, and quarterly lab retesting with symptom and wearable-recovery tracking. Lifestyle-first when the data supports it.",
      },
    ],
    evidenceSummary: [
      { finding: "TT improves sexual function/mood/BMD in confirmed hypogonadism", grade: "A" },
      { finding: "CV non-inferiority in at-risk men (TRAVERSE)", grade: "A" },
      { finding: "Benefit of TT in men with normal baseline levels", grade: "C" },
      { finding: "Lifestyle interventions raise testosterone meaningfully", grade: "B" },
    ],
    references: [
      {
        authors: "Lincoff AM, et al.",
        year: 2023,
        title: "Cardiovascular safety of testosterone-replacement therapy (TRAVERSE)",
        journal: "New England Journal of Medicine",
        url: "https://pubmed.ncbi.nlm.nih.gov/37326322/",
      },
      {
        authors: "Bhasin S, et al.",
        year: 2018,
        title: "Testosterone therapy in men with hypogonadism: an Endocrine Society clinical practice guideline",
        journal: "Journal of Clinical Endocrinology & Metabolism",
        url: "https://pubmed.ncbi.nlm.nih.gov/29562364/",
      },
    ],
    related: [
      { label: "Hormone Optimization", href: "/hormone-optimization" },
      { label: "Hormone Optimization Program", href: "/programs/hormone-optimization" },
      { label: "Medical Care", href: "/medical-care" },
    ],
  },

  "menopause-hormone-therapy": {
    slug: "menopause-hormone-therapy",
    metaTitle: "Menopausal Hormone Therapy — Evidence Review | Integrated Wellness",
    metaDescription:
      "The modern evidence on menopausal hormone therapy: WHI long-term follow-up, timing hypothesis, and current position statements — physician-reviewed.",
    eyebrow: "Evidence · Hormones",
    h1: "Menopausal Hormone Therapy",
    grade: "A",
    directAnswer:
      "Menopausal hormone therapy is the most effective treatment for vasomotor symptoms and genitourinary syndrome of menopause. Long-term Women's Health Initiative follow-up (Manson 2017, JAMA) found no increase in all-cause mortality over 18 years, and current consensus holds that for healthy women under 60 or within 10 years of menopause onset, benefits generally outweigh risks — with individualized, physician-supervised decision-making.",
    sections: [
      {
        h: "How the evidence evolved",
        body: "The 2002 WHI publication triggered a collapse in hormone-therapy use based on risks observed in an older-skewing cohort. Subsequent age-stratified analyses and 18-year cumulative follow-up showed neutral all-cause mortality and a far more favorable profile for younger, recently menopausal women — the basis of the 'timing hypothesis'.",
      },
      {
        h: "Current consensus",
        body: "The North American Menopause Society's position statement supports hormone therapy as first-line for moderate-to-severe vasomotor symptoms in appropriate candidates, with formulation, dose, route and duration individualized to each woman's risk profile and goals.",
      },
      {
        h: "Limitations",
        body: "Risk profiles differ by age, time since menopause, formulation and route (transdermal vs oral). Breast cancer risk with combined therapy is duration-dependent and must be reviewed individually. Hormone therapy is not indicated solely for prevention of chronic disease.",
      },
      {
        h: "The Integrated Wellness approach",
        body: "We map the full hormonal picture — estradiol, progesterone, FSH, thyroid, cortisol rhythm and metabolic context — account for cycle phase and life stage, and have licensed clinicians walk through the evidence-based options rather than a one-size-fits-all protocol.",
      },
    ],
    evidenceSummary: [
      { finding: "MHT is most effective treatment for vasomotor symptoms", grade: "A" },
      { finding: "No increase in all-cause mortality at 18-year WHI follow-up", grade: "A" },
      { finding: "More favorable benefit/risk when started <60 or <10y post-onset", grade: "B" },
      { finding: "MHT for chronic-disease prevention alone", grade: "C" },
    ],
    references: [
      {
        authors: "Manson JE, et al.",
        year: 2017,
        title: "Menopausal hormone therapy and long-term all-cause and cause-specific mortality (WHI)",
        journal: "JAMA",
        url: "https://pubmed.ncbi.nlm.nih.gov/28898378/",
      },
      {
        authors: "Rossouw JE, et al. (WHI Investigators)",
        year: 2002,
        title: "Risks and benefits of estrogen plus progestin in healthy postmenopausal women",
        journal: "JAMA",
        url: "https://pubmed.ncbi.nlm.nih.gov/12117397/",
      },
      {
        authors: "The North American Menopause Society",
        year: 2022,
        title: "The 2022 hormone therapy position statement",
        journal: "Menopause",
        url: "https://pubmed.ncbi.nlm.nih.gov/35797481/",
      },
    ],
    related: [
      { label: "Hormone Optimization", href: "/hormone-optimization" },
      { label: "Medical Care", href: "/medical-care" },
    ],
  },

  "gut-microbiome": {
    slug: "gut-microbiome",
    metaTitle: "The Gut Microbiome & Systemic Health — Evidence Review | Integrated Wellness",
    metaDescription:
      "What the evidence shows about the gut microbiome: composition, the gut-brain axis, diet-driven modifiability and testing limitations. Physician-reviewed.",
    eyebrow: "Evidence · Microbiome",
    h1: "The Gut Microbiome & Systemic Health",
    grade: "B",
    directAnswer:
      "Humans host roughly as many bacterial cells as human cells (Sender 2016), and the gut microbiome participates in digestion, immune regulation, metabolism and gut-brain signaling (Cryan 2019). Diet measurably reshapes microbial composition within weeks (Valdes 2018, BMJ). Testing is genuinely informative for guiding nutrition strategy — but it is an assessment tool, not a diagnostic, and single-sample results require careful interpretation.",
    sections: [
      {
        h: "What is established",
        body: "The revised cell-count estimates put bacteria at ~38 trillion cells — a 1:1 order of magnitude with human cells. Microbial genes vastly outnumber human genes, and microbial metabolites (short-chain fatty acids, neurotransmitter precursors) have measurable systemic effects on immunity, metabolism and the nervous system.",
      },
      {
        h: "The gut-brain axis",
        body: "Cryan's comprehensive Physiological Reviews synthesis documents bidirectional gut-brain communication via the vagus nerve, immune signaling and microbial metabolites, with animal and growing human evidence connecting microbiome composition to stress physiology, mood and cognition.",
      },
      {
        h: "Modifiability",
        body: "Dietary fiber diversity, fermented foods and overall dietary pattern shift microbiome composition within weeks — making the microbiome one of the most modifiable systems in the body, and dietary intervention the best-supported lever.",
      },
      {
        h: "Limitations",
        body: "A single stool sample is a snapshot of a dynamic ecosystem; healthy microbiome composition varies widely between individuals; and direct causal evidence in humans for many microbiome-condition links is still developing. Generic probiotic supplementation unmatched to sequencing results has inconsistent evidence.",
      },
      {
        h: "The Integrated Wellness approach",
        body: "We sequence composition and functional capacity, read the results against inflammatory labs, food-response and recovery data in the Matrix Engine, and target intervention at the specific functions your ecosystem is missing — then retest to confirm the shift.",
      },
    ],
    evidenceSummary: [
      { finding: "Microbiome participates in immunity, metabolism & gut-brain signaling", grade: "B" },
      { finding: "Diet reshapes microbial composition within weeks", grade: "A" },
      { finding: "Targeted testing-guided intervention beats generic probiotics", grade: "Emerging" },
      { finding: "Microbiome as standalone diagnostic", grade: "C" },
    ],
    references: [
      {
        authors: "Sender R, Fuchs S, Milo R",
        year: 2016,
        title: "Revised estimates for the number of human and bacteria cells in the body",
        journal: "PLoS Biology",
        url: "https://pubmed.ncbi.nlm.nih.gov/27541692/",
      },
      {
        authors: "Cryan JF, et al.",
        year: 2019,
        title: "The microbiota-gut-brain axis",
        journal: "Physiological Reviews",
        url: "https://pubmed.ncbi.nlm.nih.gov/31460832/",
      },
      {
        authors: "Valdes AM, et al.",
        year: 2018,
        title: "Role of the gut microbiota in nutrition and health",
        journal: "BMJ",
        url: "https://pubmed.ncbi.nlm.nih.gov/29899036/",
      },
    ],
    related: [
      { label: "Microbiome Testing", href: "/microbiome-testing" },
      { label: "Metabolic Health", href: "/metabolic-health" },
    ],
  },

  "wearables-hrv-clinical": {
    slug: "wearables-hrv-clinical",
    metaTitle: "HRV, Sleep & Wearable Data in Clinical Context — Evidence Review | Integrated Wellness",
    metaDescription:
      "Can wearable data be used clinically? Evidence on heart rate variability, sleep duration, cardiorespiratory fitness and mortality — physician-reviewed.",
    eyebrow: "Evidence · Wearables",
    h1: "HRV, Sleep & Wearable Data in Clinical Context",
    grade: "B",
    directAnswer:
      "Wearable-derived signals map onto well-validated physiology: heart rate variability reflects autonomic function (Shaffer & Ginsberg 2017), adult sleep of 7+ hours is a consensus health recommendation (Watson 2015), and cardiorespiratory fitness shows one of the strongest inverse associations with long-term mortality ever measured (Mandsager 2018). The evidence supports wearables as longitudinal context for clinical decisions — not as standalone diagnostics.",
    sections: [
      {
        h: "Heart rate variability",
        body: "HRV metrics (RMSSD, SDNN, frequency-domain measures) index parasympathetic function and autonomic balance. Standards exist for measurement and interpretation, and low HRV is associated with worse cardiovascular outcomes — though individual baselines vary widely, making trends more informative than single readings.",
      },
      {
        h: "Sleep",
        body: "Joint AASM/Sleep Research Society consensus recommends adults sleep 7 or more hours per night on a regular basis; chronic shorter sleep is associated with weight gain, diabetes, hypertension, heart disease, stroke, depression and impaired immune function. Consumer sleep staging is approximate, but duration and regularity tracking is reliable and actionable.",
      },
      {
        h: "Cardiorespiratory fitness",
        body: "In a cohort of 122,007 patients, Mandsager found cardiorespiratory fitness inversely associated with long-term mortality with no observed upper limit of benefit — elite fitness was associated with the lowest risk. VO2-max-linked wearable estimates make this the most outcome-relevant number many wearables report.",
      },
      {
        h: "Limitations",
        body: "Consumer devices vary in accuracy by metric and by manufacturer; sleep-stage classification is substantially less accurate than polysomnography; and none of these signals diagnose disease. Their power is longitudinal: trends, deviations from personal baseline, and response to interventions.",
      },
      {
        h: "The Integrated Wellness approach",
        body: "Apple Health, Oura, WHOOP, Fitbit and Garmin streams flow into your Digital Twin, where the Matrix Engine reads recovery, sleep and fitness trends against labs and biometrics — surfacing divergences for clinician review instead of treating any single reading as a verdict.",
      },
    ],
    evidenceSummary: [
      { finding: "HRV indexes autonomic function; low HRV linked to worse CV outcomes", grade: "B" },
      { finding: "7+ hours sleep consensus recommendation for adults", grade: "A" },
      { finding: "Cardiorespiratory fitness inversely predicts mortality", grade: "A" },
      { finding: "Consumer wearables as standalone diagnostics", grade: "C" },
    ],
    references: [
      {
        authors: "Shaffer F, Ginsberg JP",
        year: 2017,
        title: "An overview of heart rate variability metrics and norms",
        journal: "Frontiers in Public Health",
        url: "https://pubmed.ncbi.nlm.nih.gov/29034226/",
      },
      {
        authors: "Watson NF, et al.",
        year: 2015,
        title: "Recommended amount of sleep for a healthy adult: AASM/SRS consensus statement",
        journal: "Sleep",
        url: "https://pubmed.ncbi.nlm.nih.gov/26039963/",
      },
      {
        authors: "Mandsager K, et al.",
        year: 2018,
        title: "Association of cardiorespiratory fitness with long-term mortality among adults undergoing exercise treadmill testing",
        journal: "JAMA Network Open",
        url: "https://pubmed.ncbi.nlm.nih.gov/30646252/",
      },
    ],
    related: [
      { label: "Wearable Integrations", href: "/wearable-integrations" },
      { label: "Digital Twin Platform", href: "/digital-twin" },
      { label: "Performance Program", href: "/programs/performance-optimization" },
    ],
  },
};

export const EVIDENCE_SLUGS = Object.keys(EVIDENCE_PAGES);
